import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DocumentsTable, { Document } from '../components/Documents/DocumentsTable';
import UploadModal from '../components/Documents/UploadModal';
import FilterSelect from '../components/Documents/FilterSelect';
import Pagination from '../components/Documents/Pagination';
import { PlusIcon, MagnifyingGlassIcon, CalendarIcon, FileTextIcon, MixerHorizontalIcon, FileIcon, ArchiveIcon } from '@radix-ui/react-icons';
import { useAuth } from '../contexts/AuthContext';
import { getQuotaStatus } from '../services/api';
import api from '../services/api';
import config from '../config/config';

// API document type mapping
const documentTypeMapping: Record<string, string> = {
  'invoice': 'Invoice',
  'bank_statement': 'Bank Statement',
  'vat_book': 'VAT Book',
  'cashbook': 'CashBook'
};

// Available filter options
const documentTypes = ['All Types', 'Invoice', 'Bank Statement', 'VAT Book', 'CashBook'];
const apiDocumentTypes = ['all', 'invoice', 'bank_statement', 'vat_book', 'cashbook'];
const documentStatuses = ['All Statuses', 'Processed', 'Processing', 'Failed'];
const dateRanges = ['All Time', 'Today', 'Last 7 Days', 'Last 30 Days', 'Last 90 Days'];


const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All Types');
  const [selectedStatus, setSelectedStatus] = useState('All Statuses');
  const [selectedDateRange, setSelectedDateRange] = useState('All Time');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isArchived, setIsArchived] = useState(false);
  const perPageOptions = [10, 25, 50];
  
  // State for API data
  const [documents, setDocuments] = useState<Document[]>([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [documentsToday, setDocumentsToday] = useState(0);
  const [documentTypeMetrics, setDocumentTypeMetrics] = useState<Array<{type: string, count: number, pages: number}>>([]);
  const [apiTotalPages, setApiTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [isArchivingId, setIsArchivingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [quotaStatus, setQuotaStatus] = useState<any>(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(false);
  
  // Load quota status on component mount
  useEffect(() => {
    loadQuotaStatus();
  }, []);

  const loadQuotaStatus = async () => {
    setIsLoadingQuota(true);
    try {
      const status = await getQuotaStatus();
      setQuotaStatus(status);
    } catch (error) {
      console.error('Failed to load quota status:', error);
      setQuotaStatus(null);
    } finally {
      setIsLoadingQuota(false);
    }
  };
  
  const handleSelectDocument = (id:  string, checked: boolean) => {
    if (checked) {
      setSelectedDocuments([...selectedDocuments, id]);
    } else {
      setSelectedDocuments(selectedDocuments.filter(docId => docId !== id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDocuments(filteredDocuments.map(doc => doc.id));
    } else {
      setSelectedDocuments([]);
    }
  };



  const handleRetryDocument = async (documentId: string) => {
    try {
      await api.post(`${config.apiBaseUrl}/documents/${documentId}/retry`);
      // Refresh the documents list
      fetchDocuments();
    } catch (error) {
      console.error('Error retrying document:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  };

  const handleToggleArchive = async (documentId: string) => {
    try {
      setIsArchivingId(documentId);
      setError(null);
      const response = await api.post(`${config.apiBaseUrl}/documents/${documentId}/toggle-archive`);
      
      // Show success message
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000); // Hide after 3 seconds
      
      // Refresh the documents list
      fetchDocuments();
      
      // Clear selection if the document was selected
      setSelectedDocuments(selectedDocuments.filter(id => id !== documentId));
    } catch (error) {
      console.error('Error toggling archive status:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      setTimeout(() => setError(null), 3000); // Hide error after 3 seconds
    } finally {
      setIsArchivingId(null);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      setIsDeletingId(documentId);
      setError(null);
      await api.delete(`${config.apiBaseUrl}/documents/${documentId}`);
      
      // Show success message
      setSuccessMessage('Document deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000); // Hide after 3 seconds
      
      // Refresh the documents list
      fetchDocuments();
      
      // Clear selection if the deleted document was selected
      setSelectedDocuments(selectedDocuments.filter(id => id !== documentId));
    } catch (error) {
      console.error('Error deleting document:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      setTimeout(() => setError(null), 3000); // Hide error after 3 seconds
    } finally {
      setIsDeletingId(null);
    }
  };
  
  const handleDocumentClick = (documentId: string, documentType: string, status: string) => {
    // Don't allow navigation if document is still processing or failed
    if (status === 'Processing' || status === 'Failed') {
      return;
    }
    
    const type = documentType.toLowerCase();
  
    if (type === 'invoice') {
      navigate(`/invoice/${documentId}`);
    } else if (type === 'bank statement') {
      navigate(`/bank-statement/${documentId}`);
    } else if (type === 'cashbook') {
      navigate(`/cashbook/${documentId}`);
    } else if (type === 'vat book') {
      navigate(`/vatbook/${documentId}`);
    } else {
      navigate(`/data?type=${type.replace(' ', '-')}&id=${documentId}`);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Function to fetch documents from API
  const fetchDocuments = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Prepare API parameters
      const apiType = selectedType === 'All Types' ? '' : apiDocumentTypes[documentTypes.indexOf(selectedType)];
      const apiStatus = selectedStatus === 'All Statuses' ? '' : selectedStatus.toLowerCase();
      const apiIsArchived = isArchived ? 'true' : 'false';
      
      // Build query params
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        ...(apiType && { type: apiType }),
        ...(apiStatus && { status: apiStatus }),
        ...(apiIsArchived && { is_archived: apiIsArchived })
      };
      
      // Make API request
      const response = await api.get(`${config.apiBaseUrl}/documents/`, { params });
      const data = response.data;
      
      // Format document data
      const formattedDocuments = data.documents.map((doc: any) => ({
        ...doc,
        // Convert API type to display type
        type: documentTypeMapping[doc.type] || doc.type
      }));
      
      setDocuments(formattedDocuments);
      setTotalDocuments(data.total);
      setApiTotalPages(Math.ceil(data.total / itemsPerPage));
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      setDocuments([]);
      setTotalDocuments(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch documents when component mounts or filters change
  useEffect(() => {
    if (currentUser) {
      fetchDocuments();
      fetchMetrics();
    }
  }, [currentPage, itemsPerPage, selectedType, selectedStatus, isArchived, currentUser]);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType, selectedStatus, selectedDateRange, isArchived]);

  // Filter documents by search term client-side
  const filteredDocuments = documents.filter(doc => {
    // Only apply search filter client-side
    return searchTerm === '' || 
           doc.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
           doc.type.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Fetch metrics from API
  const fetchMetrics = async () => {
    try {
      const response = await api.get(`${config.apiBaseUrl}/documents/metrics`);
      const data = response.data;
      
      setTotalDocuments(data.total_documents);
      setTotalPages(data.total_pages);
      setDocumentsToday(data.documents_today);
      
      interface DocumentMetric {
        type: string;
        count: number;
        pages: number;
      }

      // Transform API metrics to match UI format
      const transformedMetrics = data.document_types.map((metric: DocumentMetric) => ({
        type: documentTypeMapping[metric.type] || metric.type,
        count: metric.count,
        pages: metric.pages
      }));
      setDocumentTypeMetrics(transformedMetrics);
      
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Documents</h1>
        <div className="flex space-x-3">

          <button
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary ${
              quotaStatus?.quota_exceeded 
                ? 'text-gray-400 bg-gray-300 dark:bg-gray-600 cursor-not-allowed' 
                : 'text-white bg-primary hover:bg-blue-600'
            }`}
            onClick={() => setIsUploadModalOpen(true)}
            disabled={quotaStatus?.quota_exceeded}
            title={quotaStatus?.quota_exceeded ? `Quota exceeded. You have used ${quotaStatus.pages_used}/${quotaStatus.pages_quota} pages.` : ''}
          >
            <PlusIcon className="w-5 h-5 mr-1" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        {/* Total Documents Card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow dark:shadow-gray-900/30">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Total Documents</h2>
          <p className="mt-2 text-3xl font-bold text-primary">{totalDocuments}</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{documentsToday} uploaded today</p>
        </div>
        
        {/* Total Pages Card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow dark:shadow-gray-900/30">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Total Pages</h2>
          <p className="mt-2 text-3xl font-bold text-primary">{totalPages}</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Across all documents</p>
        </div>
        
        {/* Document Types Card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow dark:shadow-gray-900/30">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Document Types</h2>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
            {documentTypeMetrics.map((item) => (
              <div key={item.type} className="flex justify-between items-center">
                <div className="flex items-center">
                  <FileIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">{item.type}</span>
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.count}/{item.pages}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-3">
          <FilterSelect 
            options={dateRanges}
            value={selectedDateRange}
            onChange={setSelectedDateRange}
            placeholder="Date Range"
            icon={<CalendarIcon className="h-4 w-4" />}
          />
          
          <FilterSelect 
            options={documentTypes}
            value={selectedType}
            onChange={setSelectedType}
            placeholder="Document Type"
            icon={<FileTextIcon className="h-4 w-4" />}
          />
          
          <FilterSelect 
            options={documentStatuses}
            value={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="Status"
            icon={<MixerHorizontalIcon className="h-4 w-4" />}
          />

          <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
            <ArchiveIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Show Archived</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={isArchived}
                onChange={() => {
                  setIsArchived(!isArchived);
                  setCurrentPage(1); // Reset to first page when toggling archive filter
                }}
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
        <div className="flex items-center bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 w-64">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-300 mr-2" />
          <input
            type="text"
            placeholder="Search by name..."
            className="flex-1 outline-none text-sm text-gray-700 dark:text-gray-200 bg-transparent placeholder-gray-400 dark:placeholder-gray-400"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Show</span>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 focus:outline-none focus:ring-primary focus:border-primary"
          >
            {perPageOptions.map(option => (
              <option key={option} value={option} className="dark:bg-gray-700">{option}</option>
            ))}
          </select>
          <span className="text-sm text-gray-500 dark:text-gray-400">entries</span>
        </div>
        {selectedDocuments.length > 0 && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {selectedDocuments.length} {selectedDocuments.length === 1 ? 'item' : 'items'} selected
          </div>
        )}
      </div>
      
      <DocumentsTable 
        documents={filteredDocuments} 
        selectedDocuments={selectedDocuments}
        onSelectDocument={handleSelectDocument}
        onSelectAll={handleSelectAll}
        onDocumentClick={handleDocumentClick}
        onRetryDocument={handleRetryDocument}
        onDeleteDocument={handleDeleteDocument}
        onToggleArchive={handleToggleArchive}
        isDeletingId={isDeletingId}
        isArchivingId={isArchivingId}
        isArchived={isArchived}
      />

      <Pagination 
        currentPage={currentPage}
        totalPages={apiTotalPages}
        onPageChange={handlePageChange}
      />
      
      <UploadModal 
        open={isUploadModalOpen} 
        onOpenChange={setIsUploadModalOpen}
        onUploadSuccess={() => {
          fetchDocuments();
          loadQuotaStatus(); // Refresh quota status after upload
        }}
      />
      
      {/* Loading and error states */}
      {isLoading && (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
      
      {/* Error and Success Messages */}
      <div className="fixed bottom-4 right-4 space-y-2">
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md shadow-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
        {successMessage && (
          <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-md shadow-lg">
            <p className="text-sm text-green-600 dark:text-green-400">{successMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;
